<template>
  <section class="chat-admin">
    <header class="chat-admin__header">
      <h3 class="chat-admin__title">CRESiteIQ Assistant</h3>
      <p class="chat-admin__subtitle">
        Enable the AI chatbot for users on this client and tune the thresholds
        used by the underutilized-space and (future) overpaying-leases queries.
      </p>
    </header>

    <div class="chat-admin__row">
      <label class="chat-admin__label" for="chatbot-enabled">Enable chatbot</label>
      <themed-toggle
        id="chatbot-enabled"
        :value="form.chatbot_enabled"
        @input="updateField('chatbot_enabled', $event)"
      />
    </div>

    <div class="chat-admin__row">
      <label class="chat-admin__label" for="chatbot-overpay">
        Overpay threshold (%)
      </label>
      <input
        id="chatbot-overpay"
        type="number"
        step="0.1"
        min="0"
        max="100"
        class="chat-admin__input"
        :value="form.chatbot_overpay_threshold"
        :disabled="!form.chatbot_enabled"
        @input="updateField('chatbot_overpay_threshold', $event.target.value)"
      />
    </div>

    <div class="chat-admin__row">
      <label class="chat-admin__label" for="chatbot-underutilized">
        Underutilized threshold (%)
      </label>
      <input
        id="chatbot-underutilized"
        type="number"
        step="0.1"
        min="0"
        max="100"
        class="chat-admin__input"
        :value="form.chatbot_underutilized_threshold"
        :disabled="!form.chatbot_enabled"
        @input="updateField('chatbot_underutilized_threshold', $event.target.value)"
      />
    </div>

    <p v-if="message" :class="['chat-admin__msg', success ? 'chat-admin__msg--ok' : 'chat-admin__msg--err']">
      {{ message }}
    </p>
  </section>
</template>

<script>
/**
 * Inline admin form for chatbot-related client settings. Designed to be
 * dropped into the existing client edit page; the parent owns persistence
 * via @input events so the chatbot section just emits changes through.
 *
 * Three fields, all backed by columns on the `clients` table:
 *   - chatbot_enabled (boolean)
 *   - chatbot_overpay_threshold (decimal 5,2)
 *   - chatbot_underutilized_threshold (decimal 5,2)
 */
export default {
  name: 'ChatAdminSettings',

  props: {
    initial: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      form: {
        chatbot_enabled: !!(this.initial.chatbot_enabled),
        chatbot_overpay_threshold: this.initial.chatbot_overpay_threshold ?? 10,
        chatbot_underutilized_threshold: this.initial.chatbot_underutilized_threshold ?? 75,
      },
      message: '',
      success: false,
    };
  },

  watch: {
    initial: {
      deep: true,
      handler(next) {
        this.form = {
          chatbot_enabled: !!(next.chatbot_enabled),
          chatbot_overpay_threshold: next.chatbot_overpay_threshold ?? 10,
          chatbot_underutilized_threshold: next.chatbot_underutilized_threshold ?? 75,
        };
      },
    },
  },

  methods: {
    updateField(field, value) {
      // Coerce numerics so the parent receives the right type.
      if (field !== 'chatbot_enabled') {
        const n = parseFloat(value);
        this.form[field] = Number.isFinite(n) ? n : value;
      } else {
        this.form[field] = !!value;
      }
      this.$emit('change', { ...this.form });
      this.$emit('update:' + field, this.form[field]);
    },
  },
};
</script>
